import { db } from '../firebase/config';
import { collection, addDoc, serverTimestamp, writeBatch, doc, getDocs, query, where } from 'firebase/firestore';

const generatePostingSetId = () => `pst_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;

const LEDGER_COLLECTION = 'ledger';

export const appendEntry = async (entry) => {
  const payload = {
    postedAt: entry.postedAt || new Date().toISOString().slice(0, 10),
    currency: 'USD',
    amountUsd: Number(entry.amountUsd), // signed
    directAccountType: entry.directAccountType, // 'company' | 'partner' | 'vendor'
    directAccountId: entry.directAccountId,
    subAccount: entry.subAccount || null,
    source: entry.source, // 'Contract' | 'Banking-E' | 'Manual'
    eventType: entry.eventType, // e.g., 'Invoiced' | 'Paid' | 'Partial' | 'Cancelled' | 'Bank-E' | 'Manual'
    memo: entry.memo || '',
    serviceId: entry.serviceId || null,
    serviceName: entry.serviceName || '',
    contractId: entry.contractId || null,
    paymentLineId: entry.paymentLineId || null,
    bankLineId: entry.bankLineId || null,
    isReversal: Boolean(entry.isReversal) || false,
    reversalOf: entry.reversalOf || null,
    createdAt: serverTimestamp(),
    createdBy: entry.createdBy || null,
  };
  const ref = await addDoc(collection(db, LEDGER_COLLECTION), payload);
  return { id: ref.id, ...payload };
};

export const appendPostingSet = async (entries, metadata) => {
  const batch = writeBatch(db);
  const createdAt = serverTimestamp();
  const postingSetId = metadata?.postingSetId || generatePostingSetId();
  const ids = [];
  
  // 🔍 RACE CONDITION LOG: Track batch ledger posting
  const totalAmount = entries.reduce((sum, e) => sum + Number(e.amountUsd || 0), 0);
  console.log('[ledgerService][appendPostingSet] BATCH COMMIT START:', {
    timestamp: new Date().toISOString(),
    postingSetId,
    contractId: metadata?.contractId,
    milestoneId: metadata?.milestoneId,
    source: metadata?.source,
    eventType: metadata?.eventType,
    entryCount: entries.length,
    totalAmount,
    entries: entries.map(e => ({
      amountUsd: e.amountUsd,
      accountType: e.directAccountType,
      accountId: e.directAccountId,
      memo: e.memo,
      eventType: e.eventType
    }))
  });
  
  entries.forEach((e) => {
    const ref = doc(collection(db, LEDGER_COLLECTION));
    ids.push(ref.id);
    batch.set(ref, {
      postedAt: e.postedAt || metadata?.postedAt || new Date().toISOString().slice(0, 10),
      currency: 'USD',
      amountUsd: Number(e.amountUsd),
      directAccountType: e.directAccountType,
      directAccountId: e.directAccountId,
      subAccount: e.subAccount || null,
      source: e.source || metadata?.source,
      eventType: e.eventType || metadata?.eventType,
      memo: e.memo || metadata?.memo || '',
      serviceId: e.serviceId || metadata?.serviceId || null,
      serviceName: e.serviceName || metadata?.serviceName || '',
      contractId: e.contractId || metadata?.contractId || null,
      paymentLineId: e.paymentLineId || metadata?.paymentLineId || null,
      milestoneId: e.milestoneId || metadata?.milestoneId || null,
      bankLineId: e.bankLineId || metadata?.bankLineId || null,
      isReversal: Boolean(e.isReversal) || false,
      reversalOf: e.reversalOf || null,
      postingSetId,
      createdAt,
      createdBy: e.createdBy || metadata?.createdBy || null,
    });
  });
  
  await batch.commit();
  
  console.log('[ledgerService][appendPostingSet] BATCH COMMIT SUCCESS:', {
    timestamp: new Date().toISOString(),
    postingSetId,
    contractId: metadata?.contractId,
    milestoneId: metadata?.milestoneId,
    ledgerIds: ids,
    totalAmount
  });
  
  return ids;
};

export const appendReversalsForPaymentLine = async ({ paymentLineId, createdBy, memo }) => {
  const q = query(collection(db, LEDGER_COLLECTION), where('paymentLineId', '==', paymentLineId));
  const snap = await getDocs(q);
  const original = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (original.length === 0) return [];
  const reversals = original.map((row) => ({
    amountUsd: -Number(row.amountUsd),
    directAccountType: row.directAccountType,
    directAccountId: row.directAccountId,
    subAccount: row.subAccount || null,
    source: 'Contract',
    eventType: 'Cancelled',
    memo: memo || `Reversal of ${row.id}`,
    contractId: row.contractId,
    paymentLineId: row.paymentLineId,
    milestoneId: row.milestoneId || null,
    bankLineId: null,
    isReversal: true,
    reversalOf: row.id,
    createdBy,
  }));
  await appendPostingSet(reversals, { postedAt: new Date().toISOString().slice(0, 10) });
  return reversals;
};

export const appendReversalsForContractEvent = async ({ contractId, paymentLineId = null, milestoneId = null, createdBy, memo }) => {
  const q = query(collection(db, LEDGER_COLLECTION), where('contractId', '==', contractId));
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    .filter((r) => (paymentLineId && r.paymentLineId === paymentLineId) || (milestoneId && r.milestoneId === milestoneId));
  if (rows.length === 0) return [];
  const reversals = rows.map((row) => ({
    amountUsd: -Number(row.amountUsd),
    directAccountType: row.directAccountType,
    directAccountId: row.directAccountId,
    subAccount: row.subAccount || null,
    source: 'Contract',
    eventType: 'Cancelled',
    memo: memo || `Reversal of ${row.id}`,
    contractId: row.contractId,
    paymentLineId: row.paymentLineId,
    milestoneId: row.milestoneId || null,
    bankLineId: null,
    isReversal: true,
    reversalOf: row.id,
    createdBy,
  }));
  await appendPostingSet(reversals, { postedAt: new Date().toISOString().slice(0, 10) });
  return reversals;
};

export default { appendEntry, appendPostingSet, appendReversalsForPaymentLine, appendReversalsForContractEvent };


